// Illustrated glass-tile artwork for the Home Hub tiles (used by Hub.jsx).
// Each tile gets a relevant illustration in the Encon orange + purple palette, with a
// dark and a light variant driven by CSS custom properties (--hta-*).
// Staffology and CRM use their exact brand colours and show the real logo file on a glass badge.

const THEME_VARS = {
  dark: {
    '--hta-ln': 'rgba(255,255,255,0.11)',
    '--hta-panel': 'rgba(255,255,255,0.10)',
    '--hta-pnb': 'rgba(255,255,255,0.24)',
    '--hta-mut': 'rgba(255,255,255,0.30)',
    '--hta-or1': '#ffa25c',
    '--hta-or2': '#e07b2e',
    '--hta-pu': '#b39bff',
    '--hta-rd': '#ff6b5a',
    '--hta-f1': 'rgba(255,255,255,0.26)',
    '--hta-f2': 'rgba(255,255,255,0.09)',
    '--hta-f3': 'rgba(255,255,255,0.16)',
    '--hta-edge': 'rgba(255,255,255,0.40)',
    '--hta-pg': 'rgba(255,255,255,0.14)',
    '--hta-cb2': '#8db8ee',
  },
  light: {
    '--hta-ln': 'rgba(120,75,30,0.16)',
    '--hta-panel': 'rgba(255,255,255,0.75)',
    '--hta-pnb': 'rgba(224,123,46,0.30)',
    '--hta-mut': 'rgba(110,75,40,0.28)',
    '--hta-or1': '#ea8433',
    '--hta-or2': '#c85f12',
    '--hta-pu': '#6d4bd1',
    '--hta-rd': '#d9432f',
    '--hta-f1': 'rgba(255,255,255,0.95)',
    '--hta-f2': 'rgba(224,123,46,0.12)',
    '--hta-f3': 'rgba(224,123,46,0.22)',
    '--hta-edge': 'rgba(140,80,20,0.40)',
    '--hta-pg': 'rgba(255,255,255,0.92)',
    '--hta-cb2': '#2c5a91',
  },
};

const TILE_ART_CSS = `
.hta-ln{fill:none;stroke:var(--hta-ln);stroke-width:1}
.hta-pn{fill:var(--hta-panel);stroke:var(--hta-pnb);stroke-width:1}
.hta-mt{fill:var(--hta-mut)}
.hta-of{fill:var(--hta-or1)}
.hta-od{fill:var(--hta-or2)}
.hta-ol{fill:none;stroke:var(--hta-or1);stroke-width:2;stroke-linecap:round;stroke-linejoin:round}
.hta-pf{fill:var(--hta-pu)}
.hta-fa{fill:var(--hta-f1);stroke:var(--hta-edge);stroke-width:1;stroke-linejoin:round}
.hta-fb{fill:var(--hta-f2);stroke:var(--hta-edge);stroke-width:1;stroke-linejoin:round}
.hta-fc{fill:var(--hta-f3);stroke:var(--hta-edge);stroke-width:1;stroke-linejoin:round}
.hta-ca{fill:var(--hta-or1);stroke:var(--hta-edge);stroke-width:1;stroke-linejoin:round}
.hta-cb{fill:var(--hta-or2);fill-opacity:.75;stroke:var(--hta-edge);stroke-width:1;stroke-linejoin:round}
.hta-cc{fill:var(--hta-or2);fill-opacity:.5;stroke:var(--hta-edge);stroke-width:1;stroke-linejoin:round}
`;

const ART_MARKUP = {
  transport: `
<defs><filter id="hta-tr-gl" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="3"/></filter></defs>
<path d="M0 112 C40 100 80 122 160 100" fill="none" style="stroke:var(--hta-pu);stroke-opacity:.25;stroke-width:9;stroke-linecap:round"/>
<rect x="64" y="18" width="26" height="16" rx="3" class="hta-pf" fill-opacity=".2"/>
<rect x="122" y="80" width="24" height="16" rx="3" class="hta-pf" fill-opacity=".2"/>
<path class="hta-ln" d="M0 38 L160 58"/>
<path class="hta-ln" d="M0 92 L160 74"/>
<path class="hta-ln" d="M38 0 L56 122"/>
<path class="hta-ln" d="M98 0 L118 122"/>
<path class="hta-ln" d="M0 64 L160 30"/>
<path d="M20 100 C34 100 40 70 62 66 S92 78 112 62 S130 40 142 34" fill="none" style="stroke:var(--hta-or1);stroke-width:9;stroke-opacity:.4" filter="url(#hta-tr-gl)"/>
<path d="M20 100 C34 100 40 70 62 66 S92 78 112 62 S130 40 142 34" fill="none" style="stroke:var(--hta-or1);stroke-width:3.5;stroke-dasharray:0.1 8;stroke-linecap:round"/>
<circle cx="20" cy="100" r="4" fill="none" style="stroke:var(--hta-or1);stroke-width:2"/>
<circle cx="142" cy="34" r="10" class="hta-of" fill-opacity=".25"/>
<circle cx="142" cy="34" r="5" class="hta-of"/>
<circle cx="142" cy="34" r="2" fill="#ffffff"/>
`,
  'goods-in': `
<polygon points="80,62 136,88 80,114 24,88" class="hta-pn" fill-opacity=".5"/>
<polygon points="62,58 81,69 62,80 43,69" class="hta-fa"/>
<polygon points="43,69 62,80 62,102 43,91" class="hta-fb"/>
<polygon points="62,80 81,69 81,91 62,102" class="hta-fc"/>
<polygon points="62,36 81,47 62,58 43,47" class="hta-ca"/>
<polygon points="43,47 62,58 62,80 43,69" class="hta-cb"/>
<polygon points="62,58 81,47 81,69 62,80" class="hta-cc"/>
<polygon points="100,64 119,75 100,86 81,75" class="hta-fa"/>
<polygon points="81,75 100,86 100,108 81,97" class="hta-fb"/>
<polygon points="100,86 119,75 119,97 100,108" class="hta-fc"/>
<path d="M4 108 L28 97" fill="none" style="stroke:var(--hta-or1);stroke-width:3;stroke-dasharray:0.1 7;stroke-linecap:round"/>
<polygon points="38,93 31.3,100.2 28.1,92.8" class="hta-of"/>
`,
  'sales-hub': `
<circle cx="80" cy="60" r="34" class="hta-ln" style="stroke-dasharray:2 4"/>
<path d="M80 60 L30 34 M80 60 L130 34 M80 60 L30 86 M80 60 L130 86" fill="none" style="stroke:var(--hta-or1);stroke-opacity:.6;stroke-width:1.5;stroke-dasharray:3 4"/>
<circle cx="80" cy="60" r="26" class="hta-pn"/>
<circle cx="80" cy="60" r="16" fill="none" style="stroke:var(--hta-mut);stroke-width:7"/>
<circle cx="80" cy="60" r="16" fill="none" transform="rotate(-90 80 60)" style="stroke:var(--hta-or1);stroke-width:7;stroke-dasharray:70 100.5;stroke-linecap:round"/>
<rect x="13" y="21" width="34" height="26" rx="7" class="hta-pn"/>
<rect x="19" y="36" width="5" height="6" rx="1" class="hta-mt"/>
<rect x="26" y="32" width="5" height="10" rx="1" class="hta-mt"/>
<rect x="33" y="28" width="5" height="14" rx="1" class="hta-of"/>
<rect x="40" y="33" width="5" height="9" rx="1" class="hta-mt"/>
<rect x="113" y="21" width="34" height="26" rx="7" class="hta-pn"/>
<polyline points="119,40 126,34 132,37 139,28 145,30" class="hta-ol"/>
<rect x="13" y="73" width="34" height="26" rx="7" class="hta-pn"/>
<circle cx="30" cy="86" r="7" fill="none" style="stroke:var(--hta-mut);stroke-width:4"/>
<circle cx="30" cy="86" r="7" fill="none" transform="rotate(-90 30 86)" style="stroke:var(--hta-pu);stroke-width:4;stroke-dasharray:26 44"/>
<rect x="113" y="73" width="34" height="26" rx="7" class="hta-pn"/>
<rect x="119" y="79" width="22" height="4" rx="2" class="hta-of"/>
<rect x="119" y="86" width="16" height="4" rx="2" class="hta-mt"/>
<rect x="119" y="93" width="26" height="4" rx="2" class="hta-mt"/>
`,
  'sales-tracker': `
<defs><linearGradient id="hta-st-area" x1="0" y1="0" x2="0" y2="1"><stop offset="0" style="stop-color:var(--hta-or1);stop-opacity:.55"/><stop offset="1" style="stop-color:var(--hta-or1);stop-opacity:0"/></linearGradient><linearGradient id="hta-st-line" x1="0" y1="0" x2="1" y2="0"><stop offset="0" style="stop-color:var(--hta-or2)"/><stop offset="1" style="stop-color:var(--hta-or1)"/></linearGradient></defs>
<path class="hta-ln" d="M12 30 H148 M12 56 H148 M12 82 H148"/>
<path class="hta-ln" d="M12 112 H148"/>
<path d="M14 98 L34 88 L54 94 L76 68 L98 74 L120 46 L120 112 L14 112 Z" fill="url(#hta-st-area)"/>
<path d="M14 98 L34 88 L54 94 L76 68 L98 74 L120 46" fill="none" stroke="url(#hta-st-line)" style="stroke-width:2.5;stroke-linecap:round;stroke-linejoin:round"/>
<path d="M120 46 L134 38 L148 26" fill="none" style="stroke:var(--hta-pu);stroke-width:2;stroke-dasharray:4 4;stroke-linecap:round"/>
<circle cx="34" cy="88" r="2.5" class="hta-pn"/>
<circle cx="54" cy="94" r="2.5" class="hta-pn"/>
<circle cx="76" cy="68" r="2.5" class="hta-pn"/>
<circle cx="98" cy="74" r="2.5" class="hta-pn"/>
<circle cx="120" cy="46" r="9" class="hta-of" fill-opacity=".25"/>
<circle cx="120" cy="46" r="4" class="hta-of"/>
<circle cx="148" cy="26" r="3.5" fill="none" style="stroke:var(--hta-pu);stroke-width:2"/>
`,
  reports: `
<rect x="44" y="16" width="70" height="92" rx="6" class="hta-pn" fill-opacity=".6" transform="rotate(-8 79 62)"/>
<rect x="52" y="12" width="70" height="96" rx="6" style="fill:var(--hta-pg);stroke:var(--hta-pnb);stroke-width:1"/>
<rect x="60" y="22" width="30" height="6" rx="3" class="hta-of"/>
<rect x="60" y="36" width="54" height="3" rx="1.5" class="hta-mt"/>
<rect x="60" y="43" width="46" height="3" rx="1.5" class="hta-mt"/>
<rect x="60" y="50" width="50" height="3" rx="1.5" class="hta-mt"/>
<rect x="60" y="57" width="38" height="3" rx="1.5" class="hta-mt"/>
<rect x="62" y="86" width="8" height="14" rx="1.5" class="hta-mt"/>
<rect x="73" y="78" width="8" height="22" rx="1.5" class="hta-mt"/>
<rect x="84" y="70" width="8" height="30" rx="1.5" class="hta-of"/>
<rect x="95" y="82" width="8" height="18" rx="1.5" class="hta-mt"/>
<path d="M60 100 H114" class="hta-ln"/>
`,
  'stock-management': `
<rect x="20" y="14" width="4" height="100" rx="2" class="hta-mt"/>
<rect x="136" y="14" width="4" height="100" rx="2" class="hta-mt"/>
<rect x="20" y="44" width="120" height="4" rx="2" class="hta-od"/>
<rect x="20" y="76" width="120" height="4" rx="2" class="hta-od"/>
<rect x="20" y="108" width="120" height="4" rx="2" class="hta-od"/>
<rect x="30" y="26" width="22" height="18" rx="2" class="hta-pn"/>
<rect x="56" y="30" width="16" height="14" rx="2" class="hta-pn"/>
<rect x="76" y="22" width="26" height="22" rx="2" class="hta-pn"/>
<rect x="106" y="28" width="20" height="16" rx="2" class="hta-pn"/>
<rect x="30" y="56" width="28" height="20" rx="2" class="hta-pn"/>
<rect x="62" y="60" width="18" height="16" rx="2" class="hta-pn"/>
<rect x="86" y="52" width="22" height="24" rx="2" class="hta-of" fill-opacity=".9"/>
<rect x="112" y="62" width="16" height="14" rx="2" class="hta-pn"/>
<rect x="30" y="90" width="18" height="18" rx="2" class="hta-pn"/>
<rect x="52" y="94" width="26" height="14" rx="2" class="hta-pn"/>
<rect x="82" y="88" width="20" height="20" rx="2" class="hta-pn"/>
<rect x="106" y="96" width="22" height="12" rx="2" class="hta-pn"/>
<path d="M97 52 V76" class="hta-ln" style="stroke:var(--hta-edge)"/>
`,
  'pi-counts': `
<defs><linearGradient id="hta-pi-scan" x1="0" y1="0" x2="0" y2="1"><stop offset="0" style="stop-color:var(--hta-or1);stop-opacity:0"/><stop offset=".5" style="stop-color:var(--hta-or1);stop-opacity:.35"/><stop offset="1" style="stop-color:var(--hta-or1);stop-opacity:0"/></linearGradient><filter id="hta-pi-gl" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="3"/></filter></defs>
<rect x="15" y="20" width="28" height="20" rx="4" class="hta-of" fill-opacity=".16" style="stroke:var(--hta-or1);stroke-opacity:.7"/>
<rect x="49" y="20" width="28" height="20" rx="4" class="hta-of" fill-opacity=".16" style="stroke:var(--hta-or1);stroke-opacity:.7"/>
<rect x="83" y="20" width="28" height="20" rx="4" class="hta-of" fill-opacity=".16" style="stroke:var(--hta-or1);stroke-opacity:.7"/>
<rect x="117" y="20" width="28" height="20" rx="4" class="hta-of" fill-opacity=".16" style="stroke:var(--hta-or1);stroke-opacity:.7"/>
<rect x="15" y="46" width="28" height="20" rx="4" class="hta-of" fill-opacity=".16" style="stroke:var(--hta-or1);stroke-opacity:.7"/>
<rect x="49" y="46" width="28" height="20" rx="4" class="hta-of" fill-opacity=".16" style="stroke:var(--hta-or1);stroke-opacity:.7"/>
<rect x="83" y="46" width="28" height="20" rx="4" class="hta-of" fill-opacity=".16" style="stroke:var(--hta-or1);stroke-opacity:.7"/>
<rect x="117" y="46" width="28" height="20" rx="4" class="hta-of" fill-opacity=".16" style="stroke:var(--hta-or1);stroke-opacity:.7"/>
<circle cx="29" cy="30" r="2.5" class="hta-of"/>
<circle cx="63" cy="30" r="2.5" class="hta-of"/>
<circle cx="97" cy="30" r="2.5" class="hta-of"/>
<circle cx="131" cy="30" r="2.5" class="hta-of"/>
<circle cx="29" cy="56" r="2.5" class="hta-of"/>
<circle cx="63" cy="56" r="2.5" class="hta-of"/>
<circle cx="97" cy="56" r="2.5" class="hta-of"/>
<circle cx="131" cy="56" r="2.5" class="hta-of"/>
<rect x="15" y="72" width="28" height="20" rx="4" class="hta-pn"/>
<rect x="49" y="72" width="28" height="20" rx="4" class="hta-pn"/>
<rect x="83" y="72" width="28" height="20" rx="4" class="hta-pn"/>
<rect x="117" y="72" width="28" height="20" rx="4" class="hta-pn"/>
<rect x="8" y="59" width="144" height="20" fill="url(#hta-pi-scan)"/>
<rect x="8" y="68" width="144" height="2" rx="1" class="hta-of" filter="url(#hta-pi-gl)"/>
<rect x="8" y="68" width="144" height="2" rx="1" class="hta-of"/>
<path d="M8 22 V13 H17 M152 22 V13 H143 M8 90 V99 H17 M152 90 V99 H143" class="hta-ol"/>
`,
  'error-log': `
<defs><linearGradient id="hta-el-line" x1="0" y1="0" x2="1" y2="0"><stop offset="0" style="stop-color:var(--hta-or2)"/><stop offset="1" style="stop-color:var(--hta-or1)"/></linearGradient></defs>
<path d="M8 32 H50 L56 32 L62 14 L70 50 L76 32 H152" fill="none" stroke="url(#hta-el-line)" style="stroke-width:2;stroke-linecap:round;stroke-linejoin:round"/>
<circle cx="62" cy="14" r="9" fill-opacity=".25" style="fill:var(--hta-rd)"/>
<circle cx="62" cy="14" r="4" style="fill:var(--hta-rd)"/>
<circle cx="18" cy="62" r="2.5" class="hta-mt"/>
<rect x="26" y="60" width="70" height="4" rx="2" class="hta-mt"/>
<rect x="10" y="70" width="140" height="16" rx="4" fill-opacity=".18" style="fill:var(--hta-rd);stroke:var(--hta-rd);stroke-opacity:.5;stroke-width:1"/>
<circle cx="18" cy="78" r="2.5" style="fill:var(--hta-rd)"/>
<rect x="26" y="76" width="92" height="4" rx="2" style="fill:var(--hta-rd)" fill-opacity=".8"/>
<circle cx="18" cy="94" r="2.5" class="hta-mt"/>
<rect x="26" y="92" width="58" height="4" rx="2" class="hta-mt"/>
<circle cx="18" cy="108" r="2.5" class="hta-mt" fill-opacity=".6"/>
<rect x="26" y="106" width="44" height="4" rx="2" class="hta-mt" fill-opacity=".6"/>
`,
  staffology: `
<defs><linearGradient id="hta-sp-grad" x1="0" y1="1" x2="1" y2="0"><stop offset="0" stop-color="#821ce8"/><stop offset="1" stop-color="#cb52e1"/></linearGradient></defs>
<path class="hta-ln" d="M52 12 V112 M84 12 V112 M116 12 V112"/>
<circle cx="16" cy="28" r="5" class="hta-mt"/>
<circle cx="16" cy="52" r="5" class="hta-mt"/>
<circle cx="16" cy="76" r="5" class="hta-mt"/>
<circle cx="16" cy="100" r="5" class="hta-mt"/>
<rect x="30" y="22" width="44" height="12" rx="6" fill="url(#hta-sp-grad)"/>
<rect x="88" y="22" width="32" height="12" rx="6" fill="url(#hta-sp-grad)" fill-opacity=".55"/>
<rect x="44" y="46" width="36" height="12" rx="6" fill="url(#hta-sp-grad)" fill-opacity=".55"/>
<rect x="92" y="46" width="48" height="12" rx="6" fill="url(#hta-sp-grad)"/>
<rect x="30" y="70" width="28" height="12" rx="6" fill="url(#hta-sp-grad)"/>
<rect x="66" y="70" width="52" height="12" rx="6" class="hta-mt"/>
<rect x="58" y="94" width="40" height="12" rx="6" fill="url(#hta-sp-grad)"/>
<rect x="104" y="94" width="30" height="12" rx="6" fill="url(#hta-sp-grad)" fill-opacity=".55"/>
<path d="M98 14 V112" fill="none" style="stroke:#cb52e1;stroke-width:1.5;stroke-dasharray:3 3"/>
<circle cx="98" cy="12" r="3.5" fill="#cb52e1"/>
`,
  crm: `
<rect x="24" y="10" width="112" height="16" rx="8" class="hta-pn"/>
<circle cx="44" cy="18" r="2" style="fill:var(--hta-cb2)"/>
<circle cx="62" cy="18" r="2" style="fill:var(--hta-cb2)"/>
<circle cx="80" cy="18" r="2" style="fill:var(--hta-cb2)"/>
<circle cx="98" cy="18" r="2" style="fill:var(--hta-cb2)"/>
<circle cx="116" cy="18" r="2" style="fill:var(--hta-cb2)"/>
<rect x="36" y="30" width="88" height="16" rx="8" fill="#396fac" fill-opacity=".4"/>
<rect x="48" y="50" width="64" height="16" rx="8" fill="#396fac" fill-opacity=".7"/>
<rect x="60" y="70" width="40" height="16" rx="8" fill="#396fac"/>
<path d="M80 86 V94" fill="none" style="stroke:var(--hta-cb2);stroke-width:2;stroke-dasharray:0.1 4;stroke-linecap:round"/>
<circle cx="80" cy="102" r="12" fill="#396fac" fill-opacity=".3"/>
<circle cx="80" cy="102" r="6" fill="#396fac"/>
<circle cx="80" cy="102" r="2" fill="#ffffff"/>
`,
  settings: `
<path class="hta-ln" d="M20 14 V104 M50 14 V104 M80 14 V104 M110 14 V104 M140 14 V104"/>
<rect x="20" y="28" width="120" height="4" rx="2" class="hta-mt"/>
<rect x="20" y="28" width="70" height="4" rx="2" class="hta-of"/>
<circle cx="90" cy="30" r="8" class="hta-pn"/>
<circle cx="90" cy="30" r="3" class="hta-of"/>
<rect x="20" y="58" width="120" height="4" rx="2" class="hta-mt"/>
<rect x="20" y="58" width="40" height="4" rx="2" class="hta-pf"/>
<circle cx="60" cy="60" r="8" class="hta-pn"/>
<circle cx="60" cy="60" r="3" class="hta-pf"/>
<rect x="20" y="88" width="120" height="4" rx="2" class="hta-mt"/>
<rect x="20" y="88" width="92" height="4" rx="2" class="hta-of"/>
<circle cx="112" cy="90" r="8" class="hta-pn"/>
<circle cx="112" cy="90" r="3" class="hta-of"/>
`,
  'company-car': `
<rect x="2" y="24" width="156" height="68" rx="30" class="hta-ln"/>
<circle cx="34" cy="58" r="16" class="hta-ln"/>
<circle cx="34" cy="58" r="22" fill="none" transform="rotate(135 34 58)" pathLength="100" style="stroke:var(--hta-mut);stroke-width:5;stroke-linecap:round;stroke-dasharray:75 100"/>
<circle cx="34" cy="58" r="22" fill="none" transform="rotate(135 34 58)" pathLength="100" style="stroke:var(--hta-or1);stroke-width:5;stroke-linecap:round;stroke-dasharray:33.75 100"/>
<path d="M7.8 66.5 L5.5 67.3 M11.8 41.8 L9.7 40.4 M34.0 30.5 L34.0 28.0 M56.2 41.8 L58.3 40.4 M60.2 66.5 L62.5 67.3" fill="none" style="stroke:var(--hta-edge);stroke-width:1;stroke-linecap:round"/>
<path d="M15.3 76.7 L12.1 79.9 M7.8 53.9 L3.4 53.2 M22.0 34.4 L19.9 30.4 M46.0 34.4 L48.1 30.4 M60.2 53.9 L64.6 53.2 M52.7 76.7 L55.9 79.9" fill="none" style="stroke:var(--hta-edge);stroke-width:1.6;stroke-linecap:round"/>
<path d="M34 58 L29.6 39.5" fill="none" style="stroke:var(--hta-or1);stroke-width:2;stroke-linecap:round"/>
<circle cx="34" cy="58" r="3.5" class="hta-of"/>
<circle cx="34" cy="58" r="1.3" fill="#ffffff"/>
<circle cx="126" cy="58" r="16" class="hta-ln"/>
<circle cx="126" cy="58" r="22" fill="none" transform="rotate(135 126 58)" pathLength="100" style="stroke:var(--hta-mut);stroke-width:5;stroke-linecap:round;stroke-dasharray:75 100"/>
<circle cx="126" cy="58" r="22" fill="none" transform="rotate(135 126 58)" pathLength="100" style="stroke:var(--hta-rd);stroke-opacity:.85;stroke-width:5;stroke-dasharray:0 63.75 11.25 100"/>
<circle cx="126" cy="58" r="22" fill="none" transform="rotate(135 126 58)" pathLength="100" style="stroke:var(--hta-pu);stroke-width:5;stroke-linecap:round;stroke-dasharray:46.50 100"/>
<path d="M99.8 66.5 L97.5 67.3 M103.8 41.8 L101.7 40.4 M126.0 30.5 L126.0 28.0 M148.2 41.8 L150.3 40.4 M152.2 66.5 L154.5 67.3" fill="none" style="stroke:var(--hta-edge);stroke-width:1;stroke-linecap:round"/>
<path d="M107.3 76.7 L104.1 79.9 M99.8 53.9 L95.4 53.2 M114.0 34.4 L111.9 30.4 M138.0 34.4 L140.1 30.4 M152.2 53.9 L156.6 53.2 M144.7 76.7 L147.9 79.9" fill="none" style="stroke:var(--hta-edge);stroke-width:1.6;stroke-linecap:round"/>
<path d="M126 58 L136.2 42.0" fill="none" style="stroke:var(--hta-or1);stroke-width:2;stroke-linecap:round"/>
<circle cx="126" cy="58" r="3.5" class="hta-of"/>
<circle cx="126" cy="58" r="1.3" fill="#ffffff"/>
<rect x="66" y="32" width="28" height="52" rx="6" class="hta-pn"/>
<rect x="71" y="38" width="18" height="4" rx="2" class="hta-mt"/>
<rect x="71" y="38" width="12" height="4" rx="2" class="hta-of"/>
<rect x="71" y="46" width="18" height="4" rx="2" class="hta-mt"/>
<rect x="71" y="46" width="8" height="4" rx="2" class="hta-pf"/>
<polyline points="71,68 76,62 81,65 89,55" class="hta-ol"/>
<circle cx="72" cy="77" r="2" class="hta-of"/>
<circle cx="80" cy="77" r="2" class="hta-pf"/>
<circle cx="88" cy="77" r="2" class="hta-mt"/>
`,
  'pi-counts-wh': `
<defs><filter id="hta-pw-gl" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="3"/></filter></defs>
<rect x="8" y="12" width="144" height="98" rx="8" class="hta-ln"/>
<rect x="24" y="22" width="12" height="78" rx="2" class="hta-pn"/>
<rect x="38" y="22" width="12" height="78" rx="2" class="hta-pn"/>
<rect x="67" y="22" width="12" height="78" rx="2" class="hta-pn"/>
<rect x="81" y="22" width="12" height="78" rx="2" class="hta-pn"/>
<rect x="110" y="22" width="12" height="78" rx="2" class="hta-pn"/>
<rect x="124" y="22" width="12" height="78" rx="2" class="hta-pn"/>
<path d="M16 105 V17 H58 V105 H101 V17" fill="none" style="stroke:var(--hta-or1);stroke-width:9;stroke-opacity:.35;stroke-linejoin:round" filter="url(#hta-pw-gl)"/>
<path d="M16 105 V17 H58 V105 H101 V17" fill="none" style="stroke:var(--hta-or1);stroke-width:3.5;stroke-dasharray:0.1 8;stroke-linecap:round;stroke-linejoin:round"/>
<circle cx="44" cy="40" r="3" class="hta-pf"/>
<circle cx="44" cy="66" r="3" class="hta-pf"/>
<circle cx="87" cy="58" r="3" class="hta-pf"/>
<circle cx="87" cy="84" r="3" class="hta-pf"/>
<circle cx="16" cy="105" r="4" fill="none" style="stroke:var(--hta-or1);stroke-width:2"/>
<circle cx="101" cy="17" r="10" class="hta-of" fill-opacity=".25"/>
<circle cx="101" cy="17" r="5" class="hta-of"/>
<circle cx="101" cy="17" r="2" fill="#ffffff"/>
`,
  'goods-in-wh': `
<rect x="10" y="8" width="142" height="52" rx="6" class="hta-ln"/>
<rect x="23" y="14" width="18" height="10" rx="2" class="hta-mt"/>
<rect x="47" y="14" width="18" height="10" rx="2" class="hta-mt"/>
<rect x="71" y="14" width="18" height="10" rx="2" class="hta-mt"/>
<rect x="95" y="14" width="18" height="10" rx="2" class="hta-mt"/>
<rect x="119" y="14" width="18" height="10" rx="2" class="hta-mt"/>
<rect x="23" y="28" width="18" height="10" rx="2" class="hta-mt"/>
<rect x="47" y="28" width="18" height="10" rx="2" class="hta-mt"/>
<rect x="71" y="28" width="18" height="10" rx="2" class="hta-mt"/>
<rect x="95" y="28" width="18" height="10" rx="2" class="hta-mt"/>
<rect x="119" y="28" width="18" height="10" rx="2" class="hta-mt"/>
<rect x="23" y="42" width="18" height="10" rx="2" class="hta-mt"/>
<rect x="47" y="42" width="18" height="10" rx="2" class="hta-mt"/>
<rect x="71" y="42" width="18" height="10" rx="2" class="hta-of"/>
<rect x="95" y="42" width="18" height="10" rx="2" class="hta-mt"/>
<rect x="119" y="42" width="18" height="10" rx="2" class="hta-mt"/>
<path d="M10 60 H152" fill="none" style="stroke:var(--hta-edge);stroke-width:2"/>
<rect x="19" y="58" width="26" height="4" rx="2" class="hta-pn"/>
<rect x="67" y="58" width="26" height="4" rx="2" class="hta-pn"/>
<rect x="115" y="58" width="26" height="4" rx="2" class="hta-pn"/>
<rect x="19" y="64" width="26" height="36" rx="4" class="hta-ln" style="stroke-dasharray:3 3"/>
<rect x="115" y="64" width="26" height="36" rx="4" class="hta-ln" style="stroke-dasharray:3 3"/>
<rect x="67" y="64" width="26" height="36" rx="4" style="fill:var(--hta-pg);stroke:var(--hta-pnb);stroke-width:1"/>
<path d="M67 76 H93 M67 88 H93" class="hta-ln"/>
<rect x="71" y="101" width="18" height="10" rx="3" class="hta-od" fill-opacity=".85"/>
<path d="M80 97 V60" fill="none" style="stroke:var(--hta-or1);stroke-width:3;stroke-dasharray:0.1 7;stroke-linecap:round"/>
<polygon points="80,53 75.5,60 84.5,60" class="hta-of"/>
`,
  'sales-dashboard': `
<g transform="translate(0 -8)">
<path d="M18 64 A30 30 0 0 1 78 64" fill="none" pathLength="100" style="stroke:var(--hta-mut);stroke-width:8;stroke-linecap:round"/>
<path d="M18 64 A30 30 0 0 1 78 64" fill="none" pathLength="100" style="stroke:var(--hta-or1);stroke-width:8;stroke-linecap:round;stroke-dasharray:72 100"/>
<path d="M48 64 L62 47" fill="none" style="stroke:var(--hta-or1);stroke-width:2;stroke-linecap:round"/>
<circle cx="48" cy="64" r="4" class="hta-of"/>
<rect x="90" y="30" width="52" height="18" rx="5" class="hta-pn"/>
<rect x="96" y="36" width="22" height="6" rx="3" class="hta-of"/>
<rect x="122" y="37" width="14" height="4" rx="2" class="hta-mt"/>
<rect x="90" y="54" width="52" height="18" rx="5" class="hta-pn"/>
<polyline points="96,67 104,61 112,64 122,58 136,60" fill="none" style="stroke:var(--hta-pu);stroke-width:2;stroke-linecap:round;stroke-linejoin:round"/>
<path d="M14 108 H146" class="hta-ln"/>
<rect x="18" y="98" width="10" height="10" rx="2" class="hta-mt"/>
<rect x="34" y="92" width="10" height="16" rx="2" class="hta-mt"/>
<rect x="50" y="96" width="10" height="12" rx="2" class="hta-mt"/>
<rect x="66" y="86" width="10" height="22" rx="2" class="hta-mt"/>
<rect x="82" y="90" width="10" height="18" rx="2" class="hta-mt"/>
<rect x="98" y="80" width="10" height="28" rx="2" class="hta-of" fill-opacity=".6"/>
<rect x="114" y="84" width="10" height="24" rx="2" class="hta-of" fill-opacity=".6"/>
<rect x="130" y="78" width="10" height="30" rx="2" class="hta-of"/>
</g>
`,
  m45: `
<rect x="14" y="12" width="20" height="6" rx="3" class="hta-mt"/>
<rect x="62" y="12" width="20" height="6" rx="3" class="hta-pf"/>
<rect x="110" y="12" width="20" height="6" rx="3" class="hta-of"/>
<rect x="14" y="24" width="42" height="18" rx="4" class="hta-pn"/>
<rect x="20" y="29" width="24" height="3" rx="1.5" class="hta-mt"/>
<rect x="20" y="35" width="15" height="3" rx="1.5" class="hta-mt"/>
<circle cx="49" cy="33" r="3" class="hta-pf"/>
<rect x="14" y="46" width="42" height="18" rx="4" class="hta-pn"/>
<rect x="20" y="51" width="24" height="3" rx="1.5" class="hta-mt"/>
<rect x="20" y="57" width="15" height="3" rx="1.5" class="hta-mt"/>
<circle cx="49" cy="55" r="3" class="hta-pf"/>
<rect x="14" y="68" width="42" height="18" rx="4" class="hta-pn"/>
<rect x="20" y="73" width="24" height="3" rx="1.5" class="hta-mt"/>
<rect x="20" y="79" width="15" height="3" rx="1.5" class="hta-mt"/>
<circle cx="49" cy="77" r="3" class="hta-pf"/>
<rect x="62" y="24" width="42" height="18" rx="4" class="hta-pn"/>
<rect x="68" y="29" width="24" height="3" rx="1.5" class="hta-mt"/>
<rect x="68" y="35" width="15" height="3" rx="1.5" class="hta-mt"/>
<circle cx="97" cy="33" r="3" class="hta-pf"/>
<rect x="62" y="46" width="42" height="18" rx="4" class="hta-of" fill-opacity=".2" style="stroke:var(--hta-or1);stroke-opacity:.8;stroke-width:1"/>
<rect x="68" y="51" width="24" height="3" rx="1.5" class="hta-mt"/>
<rect x="68" y="57" width="15" height="3" rx="1.5" class="hta-mt"/>
<circle cx="97" cy="55" r="3" class="hta-of"/>
<rect x="110" y="24" width="42" height="18" rx="4" class="hta-pn"/>
<rect x="116" y="29" width="24" height="3" rx="1.5" class="hta-mt"/>
<rect x="116" y="35" width="15" height="3" rx="1.5" class="hta-mt"/>
<circle cx="145" cy="33" r="3" class="hta-pf"/>
<rect x="14" y="100" width="138" height="6" rx="3" class="hta-mt"/>
<rect x="14" y="100" width="86" height="6" rx="3" class="hta-of"/>
`,
};

// Limited Access UI illustrations (Home, Stock Transfer, Quick Link) - same palette and classes as the tiles above.
ART_MARKUP.home = `
<path class="hta-ln" d="M8 112 H152"/>
<path class="hta-ln" d="M0 40 L160 52"/>
<path class="hta-ln" d="M28 0 L36 122"/>
<rect x="106" y="24" width="12" height="26" rx="2" class="hta-pf" fill-opacity=".85"/>
<rect x="38" y="60" width="84" height="52" rx="6" class="hta-pn"/>
<rect x="46" y="70" width="18" height="16" rx="3" class="hta-mt"/>
<rect x="96" y="70" width="18" height="16" rx="3" class="hta-mt"/>
<rect x="70" y="80" width="20" height="32" rx="4" class="hta-of"/>
<circle cx="85" cy="97" r="1.8" fill="#ffffff"/>
<path d="M24 66 L80 18 L136 66" fill="none" style="stroke:var(--hta-or1);stroke-width:5;stroke-linecap:round;stroke-linejoin:round"/>
<circle cx="80" cy="42" r="5" class="hta-of" fill-opacity=".3"/>
<circle cx="80" cy="42" r="2.5" class="hta-of"/>
`;
ART_MARKUP['stock-transfer'] = `
<path class="hta-ln" d="M0 108 H160"/>
<rect x="8" y="50" width="42" height="58" rx="6" class="hta-pn"/>
<rect x="16" y="58" width="10" height="8" rx="2" class="hta-mt"/>
<rect x="32" y="58" width="10" height="8" rx="2" class="hta-mt"/>
<rect x="16" y="72" width="10" height="8" rx="2" class="hta-mt"/>
<rect x="32" y="72" width="10" height="8" rx="2" class="hta-mt"/>
<rect x="21" y="88" width="16" height="20" rx="3" class="hta-of" fill-opacity=".9"/>
<rect x="110" y="50" width="42" height="58" rx="6" class="hta-pn"/>
<rect x="118" y="58" width="10" height="8" rx="2" class="hta-mt"/>
<rect x="134" y="58" width="10" height="8" rx="2" class="hta-mt"/>
<rect x="118" y="72" width="10" height="8" rx="2" class="hta-mt"/>
<rect x="134" y="72" width="10" height="8" rx="2" class="hta-mt"/>
<rect x="123" y="88" width="16" height="20" rx="3" class="hta-pf" fill-opacity=".85"/>
<polygon points="80,22 96,30 80,38 64,30" class="hta-ca"/>
<polygon points="64,30 80,38 80,56 64,48" class="hta-cb"/>
<polygon points="80,38 96,30 96,48 80,56" class="hta-cc"/>
<path d="M56 74 H100" fill="none" style="stroke:var(--hta-or1);stroke-width:3.5;stroke-dasharray:0.1 8;stroke-linecap:round"/>
<polygon points="108,74 98,68 98,80" class="hta-of"/>
<path d="M104 92 H60" fill="none" style="stroke:var(--hta-pu);stroke-width:3.5;stroke-dasharray:0.1 8;stroke-linecap:round"/>
<polygon points="52,92 62,86 62,98" class="hta-pf"/>
`;
ART_MARKUP['quick-link'] = `
<path class="hta-ln" d="M0 100 L160 88"/>
<path class="hta-ln" d="M40 0 L52 122"/>
<rect x="24" y="48" width="62" height="30" rx="15" fill="none" transform="rotate(-28 55 63)" style="stroke:var(--hta-or1);stroke-width:8"/>
<rect x="74" y="52" width="62" height="30" rx="15" fill="none" transform="rotate(-28 105 67)" style="stroke:var(--hta-pu);stroke-width:8"/>
<circle cx="80" cy="60" r="4" class="hta-of" fill-opacity=".35"/>
<circle cx="132" cy="28" r="3" class="hta-pf"/>
<circle cx="26" cy="96" r="3" class="hta-of"/>
<circle cx="140" cy="100" r="2" class="hta-mt"/>
`;
ART_MARKUP['b29-transfers'] = `
<defs><filter id="hta-b29-gl" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="3"/></filter></defs>
<path class="hta-ln" d="M0 110 H160"/>
<path class="hta-ln" d="M0 28 L160 40"/>
<path class="hta-ln" d="M90 0 L98 122"/>
<rect x="6" y="46" width="64" height="64" rx="6" class="hta-pn"/>
<path d="M6 54 L38 28 L70 54" fill="none" style="stroke:var(--hta-or1);stroke-width:5;stroke-linecap:round;stroke-linejoin:round"/>
<circle cx="38" cy="47" r="6" class="hta-of" fill-opacity=".3"/>
<circle cx="38" cy="47" r="3" class="hta-of"/>
<rect x="13" y="68" width="22" height="42" rx="3" class="hta-of" fill-opacity=".9"/>
<path d="M13 78 H35 M13 88 H35 M13 98 H35" class="hta-ln" style="stroke:var(--hta-edge)"/>
<rect x="41" y="68" width="22" height="42" rx="3" class="hta-mt"/>
<path d="M41 78 H63 M41 88 H63 M41 98 H63" class="hta-ln" style="stroke:var(--hta-edge)"/>
<path d="M74 72 C90 72 92 26 106 26" fill="none" style="stroke:var(--hta-or1);stroke-width:9;stroke-opacity:.4" filter="url(#hta-b29-gl)"/>
<path d="M74 72 C90 72 92 26 106 26" fill="none" style="stroke:var(--hta-or1);stroke-width:3.5;stroke-dasharray:0.1 8;stroke-linecap:round"/>
<path d="M74 76 C90 76 92 60 106 60" fill="none" style="stroke:var(--hta-or1);stroke-width:3.5;stroke-dasharray:0.1 8;stroke-linecap:round"/>
<path d="M74 80 C90 80 92 94 106 94" fill="none" style="stroke:var(--hta-pu);stroke-width:3.5;stroke-dasharray:0.1 8;stroke-linecap:round"/>
<polygon points="116,26 106,21 106,31" class="hta-of"/>
<polygon points="116,60 106,55 106,65" class="hta-of"/>
<polygon points="116,94 106,89 106,99" class="hta-pf"/>
<rect x="120" y="14" width="34" height="26" rx="5" class="hta-pn"/>
<rect x="125" y="19" width="7" height="6" rx="1.5" class="hta-mt"/>
<rect x="142" y="19" width="7" height="6" rx="1.5" class="hta-mt"/>
<rect x="132" y="29" width="10" height="11" rx="2" class="hta-of"/>
<rect x="120" y="48" width="34" height="26" rx="5" class="hta-pn"/>
<rect x="125" y="53" width="7" height="6" rx="1.5" class="hta-mt"/>
<rect x="142" y="53" width="7" height="6" rx="1.5" class="hta-mt"/>
<rect x="132" y="63" width="10" height="11" rx="2" class="hta-of"/>
<rect x="120" y="82" width="34" height="26" rx="5" class="hta-pn"/>
<rect x="125" y="87" width="7" height="6" rx="1.5" class="hta-mt"/>
<rect x="142" y="87" width="7" height="6" rx="1.5" class="hta-mt"/>
<rect x="132" y="97" width="10" height="11" rx="2" class="hta-pf" fill-opacity=".85"/>
`;
// Illustrations for the Sales Space / Admin Space link tiles (same palette and classes as the tiles above).
ART_MARKUP.quotes = `
<path class="hta-ln" d="M0 104 L160 90"/>
<path class="hta-ln" d="M22 0 L34 122"/>
<rect x="28" y="14" width="66" height="94" rx="7" style="fill:var(--hta-pg);stroke:var(--hta-pnb);stroke-width:1"/>
<rect x="37" y="24" width="28" height="6" rx="3" class="hta-of"/>
<rect x="37" y="37" width="48" height="3" rx="1.5" class="hta-mt"/>
<path d="M37 49 H85" class="hta-ln"/>
<rect x="37" y="55" width="22" height="3" rx="1.5" class="hta-mt"/><rect x="66" y="55" width="19" height="3" rx="1.5" class="hta-mt"/>
<rect x="37" y="64" width="26" height="3" rx="1.5" class="hta-mt"/><rect x="71" y="64" width="14" height="3" rx="1.5" class="hta-mt"/>
<rect x="37" y="73" width="19" height="3" rx="1.5" class="hta-mt"/><rect x="68" y="73" width="17" height="3" rx="1.5" class="hta-mt"/>
<rect x="37" y="87" width="48" height="12" rx="6" class="hta-of" fill-opacity=".92"/>
<path d="M104 30 H130 L144 46 L130 62 H104 a4 4 0 0 1 -4 -4 V34 a4 4 0 0 1 4 -4 Z" class="hta-pn"/>
<circle cx="111" cy="46" r="3.5" class="hta-pf"/>
<rect x="118" y="42" width="14" height="3.5" rx="1.75" class="hta-of"/><rect x="118" y="49" width="10" height="3.5" rx="1.75" class="hta-mt"/>
<path d="M94 66 C108 72 112 82 124 86" fill="none" style="stroke:var(--hta-or1);stroke-width:3;stroke-dasharray:.1 7;stroke-linecap:round"/>
<circle cx="132" cy="90" r="10" class="hta-of" fill-opacity=".25"/><circle cx="132" cy="90" r="5" class="hta-of"/>
<polyline points="129.2,90 131.4,92.2 135,87.6" fill="none" stroke="#ffffff" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
`;
ART_MARKUP.proforma = `
<path class="hta-ln" d="M0 100 L160 112"/>
<path class="hta-ln" d="M126 0 L116 122"/>
<rect x="30" y="12" width="72" height="98" rx="7" style="fill:var(--hta-pg);stroke:var(--hta-pnb);stroke-width:1"/>
<path d="M30 19 a7 7 0 0 1 7 -7 H95 a7 7 0 0 1 7 7 V30 H30 Z" class="hta-pf" fill-opacity=".85"/>
<rect x="38" y="18" width="24" height="5" rx="2.5" fill="#ffffff" fill-opacity=".85"/>
<rect x="38" y="40" width="40" height="3" rx="1.5" class="hta-mt"/>
<rect x="38" y="48" width="54" height="3" rx="1.5" class="hta-mt"/>
<path d="M38 59 H94" class="hta-ln"/>
<rect x="38" y="65" width="26" height="3" rx="1.5" class="hta-mt"/><rect x="72" y="65" width="22" height="3" rx="1.5" class="hta-mt"/>
<rect x="38" y="74" width="30" height="3" rx="1.5" class="hta-mt"/><rect x="76" y="74" width="18" height="3" rx="1.5" class="hta-mt"/>
<rect x="38" y="90" width="26" height="10" rx="5" class="hta-ln" style="stroke:var(--hta-or1)"/>
<g transform="rotate(-14 112 84)">
<circle cx="112" cy="84" r="24" fill="none" style="stroke:var(--hta-or1);stroke-width:2.5;stroke-opacity:.9"/>
<circle cx="112" cy="84" r="19" fill="none" style="stroke:var(--hta-or1);stroke-width:1;stroke-dasharray:2 3"/>
<circle cx="112" cy="84" r="17" class="hta-of" fill-opacity=".18"/>
<polyline points="103,85 110,92 123,76" fill="none" style="stroke:var(--hta-or1);stroke-width:4;stroke-linecap:round;stroke-linejoin:round"/>
</g>
`;
ART_MARKUP['stock-requests'] = `
<path class="hta-ln" d="M0 108 H160"/>
<path class="hta-ln" d="M0 30 L160 44"/>
<path class="hta-ln" d="M100 0 L108 122"/>
<polygon points="64,30 82,39 64,48 46,39" class="hta-ca"/>
<polygon points="46,39 64,48 64,66 46,57" class="hta-cb"/>
<polygon points="64,48 82,39 82,57 64,66" class="hta-cc"/>
<polygon points="98,38 114,46 98,54 82,46" class="hta-fa"/>
<polygon points="82,46 98,54 98,72 82,64" class="hta-fb"/>
<polygon points="98,54 114,46 114,64 98,72" class="hta-fc"/>
<path d="M14 26 H30 L40 76 H112 L124 40 H36" fill="none" style="stroke:var(--hta-or1);stroke-width:5;stroke-linecap:round;stroke-linejoin:round"/>
<circle cx="52" cy="94" r="8" class="hta-pn"/><circle cx="52" cy="94" r="3.2" class="hta-of"/>
<circle cx="102" cy="94" r="8" class="hta-pn"/><circle cx="102" cy="94" r="3.2" class="hta-of"/>
<circle cx="134" cy="26" r="13" class="hta-pf" fill-opacity=".25"/><circle cx="134" cy="26" r="9" class="hta-pf"/>
<path d="M134 21 V31 M129 26 H139" fill="none" stroke="#ffffff" stroke-width="2.4" stroke-linecap="round"/>
`;
ART_MARKUP['po-expenses'] = `
<path class="hta-ln" d="M0 28 L160 40"/>
<path class="hta-ln" d="M18 0 L30 122"/>
<path d="M30 12 H96 V104 L88 98 L80 104 L72 98 L64 104 L56 98 L48 104 L40 98 L30 104 Z" style="fill:var(--hta-pg);stroke:var(--hta-pnb);stroke-width:1;stroke-linejoin:round"/>
<rect x="38" y="22" width="18" height="6" rx="3" class="hta-of"/>
<rect x="62" y="22" width="26" height="6" rx="3" class="hta-pf" fill-opacity=".8"/>
<path d="M38 36 H88" class="hta-ln"/>
<rect x="38" y="42" width="30" height="3" rx="1.5" class="hta-mt"/><rect x="74" y="42" width="14" height="3" rx="1.5" class="hta-mt"/>
<rect x="38" y="51" width="24" height="3" rx="1.5" class="hta-mt"/><rect x="72" y="51" width="16" height="3" rx="1.5" class="hta-mt"/>
<rect x="38" y="60" width="34" height="3" rx="1.5" class="hta-mt"/><rect x="76" y="60" width="12" height="3" rx="1.5" class="hta-mt"/>
<path d="M38 71 H88" class="hta-ln"/>
<rect x="38" y="77" width="20" height="8" rx="4" class="hta-of"/><rect x="66" y="78" width="22" height="6" rx="3" class="hta-of" fill-opacity=".6"/>
<ellipse cx="124" cy="96" rx="22" ry="8" class="hta-od" fill-opacity=".8"/>
<ellipse cx="124" cy="88" rx="22" ry="8" class="hta-od" fill-opacity=".9"/>
<ellipse cx="124" cy="80" rx="22" ry="8" class="hta-of"/>
<ellipse cx="124" cy="80" rx="14" ry="4.6" fill="none" style="stroke:var(--hta-edge);stroke-width:1"/>
<path d="M104 62 C112 54 120 52 126 50" fill="none" style="stroke:var(--hta-or1);stroke-width:3;stroke-dasharray:.1 7;stroke-linecap:round"/>
<polygon points="134,48 124,43 124,54" class="hta-of"/>
`;
const TITLE_TO_KEY = {
  'transport': 'transport',
  'goods in': 'goods-in',
  'sales hub': 'sales-hub',
  'sales tracker': 'sales-tracker',
  'reports': 'reports',
  'stock management': 'stock-management',
  'pi counts': 'pi-counts',
  'error log': 'error-log',
  'crm': 'crm',
  'settings': 'settings',
  'company car': 'company-car',
  'pi counts w/h': 'pi-counts-wh',
  'goods in w/h': 'goods-in-wh',
  'sales dashboard': 'sales-dashboard',
  'm4.5': 'm45',
  'b29 transfers': 'b29-transfers',
};

// Maps a Hub tile title to its illustration key; returns null when the tile has no illustration
// (Hub.jsx then falls back to the existing icon rendering).
export const getTileArtKey = (title) => {
  const t = (title || '').trim().toLowerCase();
  if (!t) return null;
  if (t.includes('staffology')) return 'staffology';
  return TITLE_TO_KEY[t] || null;
};

// Brand-tinted glow behind the illustration for tiles that carry a third-party logo.
const TILE_INNER_TINT = {
  staffology: ['rgba(203,82,225,0.38)', 'rgba(130,28,232,0.30)'],
  crm: ['rgba(57,111,172,0.50)', 'rgba(57,111,172,0.25)'],
};

// Inner glass panel style for an illustrated tile, for the dark and light home themes.
export const getTileInnerStyle = (artKey, theme) => {
  const tint = TILE_INNER_TINT[artKey];
  const a = tint ? tint[0] : (theme === 'dark' ? 'rgba(224,123,46,0.30)' : 'rgba(234,132,51,0.22)');
  const b = tint ? tint[1] : (theme === 'dark' ? 'rgba(124,92,255,0.26)' : 'rgba(124,92,255,0.16)');
  const glow = `radial-gradient(circle at 20% 15%, ${a} 0%, rgba(0,0,0,0) 60%), radial-gradient(circle at 85% 90%, ${b} 0%, rgba(0,0,0,0) 60%)`;
  if (theme === 'dark') {
    return {
      background: `${glow}, linear-gradient(150deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.02) 100%)`,
      backdropFilter: 'blur(14px) saturate(1.3)',
      WebkitBackdropFilter: 'blur(14px) saturate(1.3)',
      border: '1px solid rgba(255,255,255,0.16)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.28), 0 0 18px rgba(224,123,46,0.18)',
    };
  }
  return {
    background: `${glow}, linear-gradient(150deg, rgba(255,255,255,0.92) 0%, rgba(243,236,228,0.92) 100%)`,
    backdropFilter: 'blur(14px) saturate(1.3)',
    WebkitBackdropFilter: 'blur(14px) saturate(1.3)',
    border: '1px solid rgba(224,123,46,0.35)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,1), 0 4px 14px rgba(190,110,40,0.18)',
  };
};

// Rendered once by Hub.jsx: shared class rules used by every illustration.
export const HubTileArtStyles = () => <style>{TILE_ART_CSS}</style>;

const BADGE_GLOW = {
  staffology: '0 0 16px rgba(203,82,225,0.65), 0 0 4px rgba(130,28,232,0.6)',
  crm: '0 0 16px rgba(57,111,172,0.75), 0 0 4px rgba(57,111,172,0.6)',
};

// Glass badge that frames the real logo file (kept in its own colours) with a brand-colour glow.
export const HubTileLogoBadge = ({ src, alt, artKey }) => (
  <div
    className="relative flex-shrink-0 w-9 h-9 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-xl overflow-hidden p-1"
    style={{
      background: 'rgba(255,255,255,0.14)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      border: '1px solid rgba(255,255,255,0.40)',
      boxShadow: `inset 0 1px 0 rgba(255,255,255,0.5), ${BADGE_GLOW[artKey] || '0 0 14px rgba(238,114,3,0.5)'}`,
    }}
  >
    <img src={src} alt={alt} className="w-full h-full object-contain rounded-lg" />
    <span
      className="absolute inset-0 pointer-events-none"
      style={{ background: 'linear-gradient(125deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0) 45%)' }}
    />
  </div>
);

const HubTileArt = ({ artKey, theme = 'light', className = '' }) => {
  const markup = ART_MARKUP[artKey];
  if (!markup) return null;
  return (
    <svg
      className={className}
      viewBox="0 2 160 118"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false"
      style={THEME_VARS[theme] || THEME_VARS.light}
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  );
};

export default HubTileArt;
